import { Col, Form, Input, Row } from "antd";
import { ReactComponent as SearchIcon } from "shared/assets/images/icon-search.svg";
import { ReactComponent as QuincyChat } from "shared/assets/images/icon-quincy-chat.svg";
import { ReactComponent as CalendarIcon } from "shared/assets/images/calendar-icon.svg";
import { ReactComponent as DiscIcon } from "shared/assets/images/disc-icon.svg";
import { ReactComponent as WalletIcon } from "shared/assets/images/wallet-icon.svg";
import { ReactComponent as HandMetalIcon } from "shared/assets/images/hand-metal-icon.svg";
import chatLogoIcon from "shared/assets/images/chat-logo-icon.png";
import { useCallback, useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { TypingEffect } from "./TypingEffect";
import Markdown from "markdown-to-jsx";
import { useTranslation } from "react-i18next";
import {
  LikeOutlined,
  DislikeOutlined,
  CopyOutlined,
  AlignCenterOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  sendMessageChat,
  getChats,
  getChatById,
  setFeedbackMessage,
} from "shared/service/StrmService";
import "./Quincy.scss";
import {
  dataLayerClickedEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { QuincyButtonClickEventName } from "event-tracker/button-click-event-name";
import ChatHistoryMenu from "./ChatHistoryMenu";

enum ChatPredefinedQuestionEnum {
  AboutMyCareerStage = 0,
  HowCanIGoToNextStage = 1,
  ExplainAboutAdvance = 2,
  Recommendations = 3,
}

enum ChatMessageFeedback {
  Like,
  Dislike,
}

interface Message {
  id: string;
  role: string;
  content: string;
  hide: boolean;
  feedback?: ChatMessageFeedback;
}

const Quincy: React.FC = (_) => {
  const { t } = useTranslation();
  const lastMessageRef = useRef<null | HTMLDivElement>(null);
  const [sendDisabled, setSendDisabled] = useState(false);
  const [chatHistoryOpen, setChatHistoryOpen] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [outputTextRealtime, setOutputTextRealtime] = useState<Message | null>(
    null
  );
  const [inputText, setInputText] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const getChatHistory = useCallback(async () => {
    setLoading(chatId === null);
    const chats: any = await getChats();
    setMenuItems(
      chats?.response?.map((x: any) => {
        return {
          key: x.id,
          label: x.title,
          createdOn: x.createdOn,
        };
      })
    );
    setLoading(false);
  }, [chatId]);

  useEffect(() => {
    getChatHistory();
  }, [getChatHistory]);

  const shouldShowSugestion = messages.length === 0;
  const receivingAnswear = outputTextRealtime || sendDisabled;
  const hasChat = menuItems?.length > 0;
  const shouldOpenHistoryChat = hasChat && (chatHistoryOpen || !isMobile);
  const isSystemMessage = (role: string) => role !== "user";

  const prepareForNewQuestion = (question: string) => {
    setSendDisabled(true);
    setInputText("");
    setOutputTextRealtime(null);
    const lastUserMessage = {
      role: "user",
      content: question,
      hide: false,
      id: "",
    };

    return lastUserMessage;
  };

  const submitQuestion = async (
    question: string,
    predefinedQuestion?: ChatPredefinedQuestionEnum
  ) => {
    if (sendDisabled || question === "") return false;
    dataLayerClickedEvent(QuincyButtonClickEventName.QUINCY_SEARCH, {
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
    let newMessages = [...messages];
    try {
      if (outputTextRealtime) {
        newMessages.push(outputTextRealtime);
      }

      let previousMessages = newMessages.length > 0 ? [...newMessages] : null;
      const userMessage = prepareForNewQuestion(question);
      newMessages.push(userMessage);
      setMessages(newMessages);

      const request = {
        previousMessages: previousMessages,
        predefinedQuestion: predefinedQuestion ?? null,
        prompt: question,
        chatId: chatId,
      };

      const answear: any = await sendMessageChat(request);
      const allMessages = answear.response.messages;
      const messageLength = allMessages.length;

      setMessages([...allMessages.slice(0, messageLength - 1)]);
      setOutputTextRealtime(getLast(allMessages));
      await getChatHistory();
      setChatId(answear.response.id);

      lastMessageRef?.current?.scrollIntoView();
    } catch (error) {
      console.error("Error during the request:", error);
    }
  };

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") {
      submitQuestion(inputText);
    }
  };

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
  }, []);

  const getLast = (arr: any[]) => {
    return arr.length > 0 ? arr[arr.length - 1] : undefined;
  };

  const updateMessageFeedbackById = (
    id: string,
    feedback?: ChatMessageFeedback
  ) => {
    const newMessages = messages.map((item) => {
      if (item.id === id) {
        item.feedback = feedback;
      }
      return item;
    });

    if (outputTextRealtime?.id === id) {
      setOutputTextRealtime({
        ...outputTextRealtime,
        feedback: feedback,
      });
    } else {
      setMessages(newMessages);
    }
  };

  const onClickNewChat = async () => {
    setChatId(null);
    setMessages([]);
    setOutputTextRealtime(null);
    setChatHistoryOpen(false);
  };

  const onClickMenuItem = async (info: any) => {
    const chatMessages: any = await getChatById(info.key);
    setChatId(info.key);
    setMessages(chatMessages.response);
    setOutputTextRealtime(null);
    setChatHistoryOpen(false);
    lastMessageRef?.current?.scrollIntoView();
  };

  const onClickLikeMessage = async (messageId: string) => {
    dataLayerClickedEvent(QuincyButtonClickEventName.QUINCY_LIKE, {
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
    updateMessageFeedbackById(messageId, ChatMessageFeedback.Like);
    await setFeedbackMessage(messageId, ChatMessageFeedback.Like);
  };

  const onClickDislikeMessage = async (messageId: string) => {
    dataLayerClickedEvent(QuincyButtonClickEventName.QUINCY_DISLIKE, {
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
    updateMessageFeedbackById(messageId, ChatMessageFeedback.Dislike);
    await setFeedbackMessage(messageId, ChatMessageFeedback.Dislike);
  };

  const onClickCopyMessage = (text: string) => {
    dataLayerClickedEvent(QuincyButtonClickEventName.QUINCY_COPY, {
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(t("pages.Quincy.textCopied"));
      })
      .catch((error) => {
        console.error("Falha ao copiar o texto:", error);
      });
  };

  const renderAswearOptions = (message: Message) => {
    return (
      <div className="answear-options" ref={lastMessageRef}>
        <LikeOutlined
          className={`${
            message.feedback === ChatMessageFeedback.Like && "active"
          }`}
          onClick={() => onClickLikeMessage(message.id)}
        />
        <DislikeOutlined
          className={`${
            message.feedback === ChatMessageFeedback.Dislike && "active"
          }`}
          onClick={() => onClickDislikeMessage(message.id)}
        />
        <CopyOutlined onClick={() => onClickCopyMessage(message.content)} />
      </div>
    );
  };

  return (
    <div
      className={`page-quincy ${isMobile && "mobile"} ${
        shouldOpenHistoryChat && "chat-history-open"
      }`}
    >
      <Row>
        <Col className="header" xs={24}>
          <h1 className="title">{t("pages.Quincy.quincy")}</h1>
          <h3 className="subtitle">{t("pages.Quincy.title")}</h3>
        </Col>
      </Row>
      {hasChat && (
        <ChatHistoryMenu
          menuItems={menuItems}
          chatIdSelected={chatId}
          onClickMenuItem={onClickMenuItem}
          onClickNewChat={onClickNewChat}
          loading={loading}
          hide={chatHistoryOpen}
          onClose={() => setChatHistoryOpen(false)}
        />
      )}

      <Row
        justify="center"
        className={`content ${isMobile && "mobile"}`}
        style={{ margin: "0 auto" }}
      >
        <div className={`chat-wrapper ${isMobile && "mobile"}`}>
          <Col className="chat-container scroll-style">
            {messages
              .filter((x) => !x.hide)
              .map((ev, index) => (
                <div
                  className={`message ${
                    isSystemMessage(ev.role) ? "message-system" : "message-user"
                  }`}
                  key={index}
                >
                  {isSystemMessage(ev.role) && (
                    <div className="message-icon">
                      <QuincyChat />
                    </div>
                  )}
                  <div className="paragraph">
                    <Markdown>{ev.content}</Markdown>
                    {isSystemMessage(ev.role) && renderAswearOptions(ev)}
                  </div>
                </div>
              ))}
            {receivingAnswear && (
              <div className={`message message-system`}>
                <div className="message-icon">
                  <QuincyChat />
                </div>
                <div className="paragraph">
                  {outputTextRealtime ? (
                    <>
                      <TypingEffect
                        fullText={outputTextRealtime.content}
                        onComplete={() => {
                          setSendDisabled(false);
                        }}
                      />
                      {renderAswearOptions(outputTextRealtime)}
                    </>
                  ) : (
                    "..."
                  )}
                </div>
              </div>
            )}
          </Col>
          {shouldShowSugestion && (
            <div style={{ width: "100%" }}>
              <Row justify="center" hidden={isMobile}>
                <Col>
                  <img
                    src={chatLogoIcon}
                    alt="chatLogoIcon"
                    className="chat-logo-icon"
                  />
                </Col>
              </Row>
              <Row className="row-sugestion" gutter={[5, 5]}>
                <Col xs={12} md={6}>
                  <div
                    className={`sugestion ${isMobile && "mobile"}`}
                    onClick={() => {
                      submitQuestion(
                        t("pages.Quincy.prefinedQuestionAboutMyCareerStage"),
                        ChatPredefinedQuestionEnum.AboutMyCareerStage
                      );
                    }}
                  >
                    <DiscIcon />
                    <p>
                      {t("pages.Quincy.prefinedQuestionAboutMyCareerStage")}
                    </p>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div
                    className={`sugestion ${isMobile && "mobile"}`}
                    onClick={() =>
                      submitQuestion(
                        t("pages.Quincy.prefinedQuestionHowCanIGoToNextStage"),
                        ChatPredefinedQuestionEnum.HowCanIGoToNextStage
                      )
                    }
                  >
                    <HandMetalIcon />
                    <p>
                      {t("pages.Quincy.prefinedQuestionHowCanIGoToNextStage")}
                    </p>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div
                    className={`sugestion ${isMobile && "mobile"}`}
                    onClick={() =>
                      submitQuestion(
                        t("pages.Quincy.prefinedQuestionExplainAboutAdvance"),
                        ChatPredefinedQuestionEnum.ExplainAboutAdvance
                      )
                    }
                  >
                    <WalletIcon />
                    <p>
                      {t("pages.Quincy.prefinedQuestionExplainAboutAdvance")}
                    </p>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div
                    className={`sugestion ${isMobile && "mobile"}`}
                    onClick={() =>
                      submitQuestion(
                        t("pages.Quincy.prefinedQuestionRecommendations"),
                        ChatPredefinedQuestionEnum.Recommendations
                      )
                    }
                  >
                    <CalendarIcon />
                    <p>{t("pages.Quincy.prefinedQuestionRecommendations")}</p>
                  </div>
                </Col>
              </Row>
            </div>
          )}
          <div className="row-search">
            <span
              className={`button-history-chat ${isMobile && "mobile"} ${
                menuItems?.length === 0 && "icon-disabled"
              }`}
            >
              <AlignCenterOutlined
                onClick={() => {
                  if (hasChat) setChatHistoryOpen(true);
                }}
              />
            </span>
            <Form.Item className={`chat-input ${isMobile && "mobile"}`}>
              <Input
                placeholder={t("pages.Quincy.placeholder")}
                className="input-text"
                suffix={
                  <SearchIcon
                    onClick={() => submitQuestion(inputText)}
                    className={`${
                      (sendDisabled || inputText === "") && "icon-disabled"
                    }`}
                  />
                }
                onChange={(ev) => setInputText(ev.target.value)}
                value={inputText}
                onKeyDown={handleKeyDown}
              />
            </Form.Item>
            <p className={`disclaimer ${isMobile && "mobile"}`}>
              {t("pages.Quincy.disclaimer")}
            </p>
          </div>
        </div>
      </Row>
    </div>
  );
};

export default Quincy;
